/*
 * Date de naissance & âge — source de vérité unique côté client.
 *
 * Le serveur ne stocke plus d'âge : il stocke une date de naissance
 * (`dateOfBirth`, ISO `yyyy-MM-dd`) et recalcule `age` à chaque lecture.
 * Le champ `age` reste accepté en écriture, mais il y signifie désormais
 * « âge approximatif quand la date de naissance est inconnue » : le
 * serveur en déduit une date estimée (1ᵉʳ janvier) et lève
 * `dateOfBirthEstimated`.
 *
 * Tout ce qui touche au couple date de naissance / âge vit ici — calcul,
 * formatage, valeur de formulaire, règle de payload — pour que les trois
 * écrans qui éditent ce couple (création patient, fiche patient, profil)
 * partagent exactement le même comportement.
 *
 * L'âge calculé ici ne sert qu'au retour visuel immédiat dans les
 * formulaires : l'âge affiché partout ailleurs est celui du serveur.
 */

/** Bornes de l'âge approximatif, alignées sur la validation du serveur. */
export const MIN_APPROXIMATE_AGE = 0;
export const MAX_APPROXIMATE_AGE = 120;

/** Valeur éditée par le champ « date de naissance / âge approximatif ». */
export interface BirthDateValue {
  /** Date de naissance exacte au format `yyyy-MM-dd`, ou `""` si inconnue. */
  dateOfBirth: string;
  /** Âge approximatif saisi, ou `""` — conservé en chaîne comme tout champ. */
  approximateAge: string;
}

/** Champs de date de naissance tels que le serveur les renvoie. */
export interface BirthDateSource {
  age?: number | null;
  dateOfBirth?: string | null;
  dateOfBirthEstimated?: boolean | null;
}

/** Payload accepté par le serveur pour le couple date de naissance / âge. */
export interface BirthDatePayload {
  dateOfBirth?: string;
  age?: number;
}

export const EMPTY_BIRTH_DATE_VALUE: BirthDateValue = {
  dateOfBirth: "",
  approximateAge: "",
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

/**
 * Découpe une date ISO `yyyy-MM-dd` en trois entiers, sans passer par
 * `new Date(...)`.
 *
 * `new Date("1984-03-12")` est interprétée en UTC par la spécification :
 * comparée à une date locale, elle décale d'un jour à l'ouest de
 * Greenwich et fait sauter l'anniversaire. On reste donc sur des entiers
 * de calendrier, qui n'ont pas de fuseau.
 *
 * Renvoie `null` si la chaîne n'est pas une date de calendrier réelle
 * (le 31 février est rejeté).
 */
const parseIsoDate = (isoDate?: string | null): CalendarDate | null => {
  if (!isoDate) return null;

  const match = ISO_DATE_PATTERN.exec(isoDate.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // `Date.UTC` normalise les débordements (32 janvier → 1ᵉʳ février) : si la
  // date normalisée ne correspond plus aux entiers d'origine, l'entrée
  // désignait un jour qui n'existe pas.
  const normalized = new Date(Date.UTC(year, month - 1, day));
  if (
    normalized.getUTCFullYear() !== year ||
    normalized.getUTCMonth() !== month - 1 ||
    normalized.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

/** Date locale du jour au format `yyyy-MM-dd` (attribut `max` du champ). */
export const todayIsoDate = (today: Date = new Date()): string => {
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
};

/**
 * Âge révolu à la date `today`, ou `null` si la date de naissance est
 * absente, mal formée ou dans le futur.
 *
 * L'anniversaire compte le jour même : né le 21 septembre, on prend un an
 * le 21 septembre, pas le 22.
 */
export const computeAgeFromBirthDate = (
  isoDate?: string | null,
  today: Date = new Date()
): number | null => {
  const birthDate = parseIsoDate(isoDate);
  if (!birthDate) return null;

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const hasHadBirthdayThisYear =
    currentMonth > birthDate.month ||
    (currentMonth === birthDate.month && currentDay >= birthDate.day);

  const age = currentYear - birthDate.year - (hasHadBirthdayThisYear ? 0 : 1);
  return age < 0 ? null : age;
};

/** Date de naissance exacte en `jj/mm/aaaa`, ou `null` si non exploitable. */
export const formatBirthDate = (isoDate?: string | null): string | null => {
  const birthDate = parseIsoDate(isoDate);
  if (!birthDate) return null;

  const month = String(birthDate.month).padStart(2, "0");
  const day = String(birthDate.day).padStart(2, "0");
  return `${day}/${month}/${birthDate.year}`;
};

/**
 * Libellé court de la date de naissance pour les en-têtes de dossier :
 * « 12/03/1984 » quand la date est exacte, « né(e) vers 1984 » quand elle a
 * été estimée à partir d'un âge. `null` quand il n'y a rien d'honnête à
 * afficher.
 */
export const formatBirthDateSummary = (
  source?: BirthDateSource | null
): string | null => {
  const birthDate = parseIsoDate(source?.dateOfBirth);
  if (!birthDate) return null;

  return source?.dateOfBirthEstimated
    ? `né(e) vers ${birthDate.year}`
    : formatBirthDate(source?.dateOfBirth);
};

/**
 * Valeur initiale du champ pour un patient chargé depuis le serveur.
 *
 * Une date estimée n'est pas présentée comme une vraie date de naissance :
 * le champ date reste vide et c'est l'âge approximatif qui est pré-rempli,
 * pour que la secrétaire complète la vraie date plutôt que de valider un
 * 1ᵉʳ janvier qu'elle n'a jamais saisi.
 */
export const toBirthDateValue = (
  source?: BirthDateSource | null
): BirthDateValue => {
  if (!source) return EMPTY_BIRTH_DATE_VALUE;

  const hasExactBirthDate =
    !source.dateOfBirthEstimated && parseIsoDate(source.dateOfBirth) !== null;

  return {
    dateOfBirth: hasExactBirthDate ? String(source.dateOfBirth) : "",
    approximateAge:
      !hasExactBirthDate && source.age != null ? String(source.age) : "",
  };
};

/**
 * Traduction de la valeur du champ en payload serveur :
 *   - date saisie → `dateOfBirth` seul (le serveur ignorerait `age`) ;
 *   - sinon âge approximatif saisi → `age` numérique seul ;
 *   - sinon aucun des deux, pour ne rien changer côté serveur.
 */
export const buildBirthDatePayload = (
  value: BirthDateValue
): BirthDatePayload => {
  const dateOfBirth = value.dateOfBirth?.trim();
  if (dateOfBirth) return { dateOfBirth };

  const approximateAge = value.approximateAge?.trim();
  if (!approximateAge) return {};

  const age = Number(approximateAge);
  return Number.isFinite(age) ? { age } : {};
};

/**
 * Message d'erreur de la valeur saisie, ou `null` si elle est envoyable.
 *
 * Les attributs `max` du champ date et `min`/`max` du champ nombre guident
 * la saisie mais ne l'empêchent pas : le serveur refuserait une date future
 * ou un âge hors bornes, autant le dire tout de suite et en français.
 */
export const validateBirthDateValue = (
  value: BirthDateValue,
  today: Date = new Date()
): string | null => {
  const dateOfBirth = value.dateOfBirth?.trim();
  if (dateOfBirth) {
    return computeAgeFromBirthDate(dateOfBirth, today) === null
      ? "Date de naissance invalide : elle ne peut pas être dans le futur."
      : null;
  }

  const approximateAge = value.approximateAge?.trim();
  if (!approximateAge) return null;

  const age = Number(approximateAge);
  if (
    !Number.isInteger(age) ||
    age < MIN_APPROXIMATE_AGE ||
    age > MAX_APPROXIMATE_AGE
  ) {
    return `Âge approximatif invalide : il doit être un nombre entier entre ${MIN_APPROXIMATE_AGE} et ${MAX_APPROXIMATE_AGE}.`;
  }

  return null;
};

/**
 * Applique la règle de payload à un objet de formulaire.
 *
 * Les écrans de mise à jour étalent l'objet renvoyé par le serveur dans
 * leur payload : on retire donc `age`, `dateOfBirth` et
 * `dateOfBirthEstimated` avant de réappliquer la règle. Sans ce nettoyage,
 * un `age` calculé par le serveur repartirait à côté d'une date de
 * naissance non modifiée, et le serveur le prendrait pour une correction
 * d'âge — il ré-estimerait la date au 1ᵉʳ janvier.
 */
export const withBirthDatePayload = <T extends BirthDateSource>(
  payload: T,
  value: BirthDateValue
): Omit<T, keyof BirthDateSource> & BirthDatePayload => {
  const {
    age: _age,
    dateOfBirth: _dateOfBirth,
    dateOfBirthEstimated: _dateOfBirthEstimated,
    ...rest
  } = payload;

  return { ...rest, ...buildBirthDatePayload(value) };
};
